/**
 * ID Generation Hook
 *
 * FERP CRITICAL: Backend ID generation for eliminating client-side ID security risk
 * Repository pattern strict enforcement
 *
 * Principles:
 * - NEVER generate IDs client-side (P0 security blocker)
 * - All IDs from backend generateId GraphQL mutation
 * - Snowflake IDs: time-sortable, collision-resistant
 * - Repository pattern (no direct API calls)
 */

import { useState, useCallback } from 'react';
import { idGenerationRepository, EntityType } from '../data/repositories';
import { BackendId, asBackendId } from '../types/branded-ids';

export interface UseGenerateIdOptions {
  entityType?: EntityType;
  autoGenerate?: boolean; // Auto-generate ID on mount
}

export interface UseGenerateIdReturn {
  // State
  id: BackendId | null;
  ids: BackendId[];
  isGenerating: boolean;
  error: Error | null;

  // Actions
  generateId: (entityType?: EntityType) => Promise<BackendId>;
  generateIds: (entityType: EntityType, count: number) => Promise<BackendId[]>;
  reset: () => void;
}

/**
 * Hook for backend ID generation
 *
 * REPOSITORY PATTERN: Uses idGenerationRepository.generateId()
 * CRITICAL: Eliminates P0 security blocker (client-side ID generation)
 *
 * @example
 * ```tsx
 * const { id, generateId, isGenerating } = useGenerateId({
 *   entityType: EntityType.workflowNode
 * });
 *
 * const handleSave = async () => {
 *   const workflowId = await generateId(); // Backend-generated Snowflake ID
 *   await createWorkflow({ id: workflowId, ... });
 * };
 * ```
 *
 * @example Batch ID generation
 * ```tsx
 * const { generateIds } = useGenerateId();
 *
 * const handleBatchCreate = async () => {
 *   const actorIds = await generateIds(EntityType.Actor, 10);
 *   await Promise.all(actorIds.map(id => createActor({ id, ... })));
 * };
 * ```
 */
export function useGenerateId(options: UseGenerateIdOptions = {}): UseGenerateIdReturn {
  const [id, setId] = useState<BackendId | null>(null);
  const [ids, setIds] = useState<BackendId[]>([]);
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  /**
   * Generate single ID
   * REPOSITORY PATTERN: Uses idGenerationRepository.generateId()
   * TYPE SAFETY: Returns BackendId (prevents arbitrary string assignments)
   */
  const generateId = useCallback(async (entityType?: EntityType): Promise<BackendId> => {
    setIsGenerating(true);
    setError(null);

    try {
      const type = entityType || options.entityType;
      if (!type) {
        throw new Error('entityType is required');
      }

      const generatedId = await idGenerationRepository.generateId(type);
      // Safe assertion: Backend-generated Snowflake ID
      const typedId = asBackendId(generatedId);
      setId(typedId);
      setIds([typedId]);

      return typedId;
    } catch (err) {
      const error = err instanceof Error ? err : new Error('Failed to generate ID');
      setError(error);
      throw error;
    } finally {
      setIsGenerating(false);
    }
  }, [options.entityType]);

  /**
   * Generate multiple IDs (batch operations)
   * REPOSITORY PATTERN: Uses idGenerationRepository.generateIds()
   * TYPE SAFETY: Returns BackendId[] (prevents arbitrary string assignments)
   */
  const generateIds = useCallback(async (entityType: EntityType, count: number): Promise<BackendId[]> => {
    setIsGenerating(true);
    setError(null);

    try {
      const result = await idGenerationRepository.generateIds(entityType, count);
      // Safe assertion: Backend-generated Snowflake IDs
      const typedIds = result.ids.map(asBackendId);
      setIds(typedIds);
      setId(typedIds[0] ?? null); // Set first ID as primary (null if empty array)

      return typedIds;
    } catch (err) {
      const error = err instanceof Error ? err : new Error('Failed to generate IDs');
      setError(error);
      throw error;
    } finally {
      setIsGenerating(false);
    }
  }, []);

  /**
   * Reset hook state
   */
  const reset = useCallback(() => {
    setId(null);
    setIds([]);
    setError(null);
  }, []);

  return {
    // State
    id,
    ids,
    isGenerating,
    error,

    // Actions
    generateId,
    generateIds,
    reset,
  };
}

/**
 * Specialized hook for single ID generation with auto-reset
 * Simpler API for common use cases
 *
 * @example
 * ```tsx
 * const newWorkflowId = useGenerateSingleId(EntityType.workflowNode);
 *
 * const handleSave = async () => {
 *   if (!newWorkflowId) return;
 *   await createWorkflow({ id: newWorkflowId, ... });
 * };
 * ```
 */
export function useGenerateSingleId(
  entityType: EntityType,
  options: { autoGenerate?: boolean } = {}
): BackendId | null {
  const { id, generateId } = useGenerateId({ entityType });

  // Auto-generate on mount if requested
  React.useEffect(() => {
    if (options.autoGenerate && !id) {
      generateId().catch(console.error);
    }
  }, [options.autoGenerate, id, generateId]);

  return id;
}

// React import for useEffect
import React from 'react';
